import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable,EventEmitter } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable()
export class RouteTrackingService {
	public appParcelsServerSideRefresh$: EventEmitter<void>;
	public stopDetailRefresh$: EventEmitter<number>;
	public showMessage$: BehaviorSubject<boolean>;
	public routeStopChanges$: BehaviorSubject<boolean>;

	constructor(private _http: HttpClient) {
		this.appParcelsServerSideRefresh$ = new EventEmitter<void>();
		this.stopDetailRefresh$ = new EventEmitter<number>();
		this.showMessage$ = new BehaviorSubject<boolean>(false);
		this.routeStopChanges$ = new BehaviorSubject<boolean>(false);
	}

	/**
	 * Signal that route stops have been modified
	 */
	markRouteStopChanged(): void {
		this.routeStopChanges$.next(true);
	}

	/**
	 * Reset the change flag
	 */
	resetRouteStopChanges(): void {
		this.routeStopChanges$.next(false);
	}

	/**
	 * @api {get} /v2/RouteStops/${id}/Receipt Get Receipt
	 * @apiName getReceipt
	 * @apiGroup RouteStops
	 * @apiVersion 20.0.0
	 */
	getReceipt(id) {
		return this._http.get<any>(`${environment.apiUrl}/v2/RouteStops/${id}/Receipt`);
	}

	/**
	 * @api {get} /v2/Routes/PostDates Get Route Post Dates
	 * @apiName getRoutePostDates
	 * @apiParam {string} [dateFrom] Date From
	 * @apiGroup Routes
	 * @apiVersion 20.0.0
	 */
	getRoutePostDates(date) {
		let params: HttpParams = new HttpParams();
		params = params.append('dateFrom', date);
		return this._http.get<any>(`${environment.apiUrl}/v2/Routes/PostDates`, {params});
	}

	/**
	 * @api {get} /v2/RouteTracking/:id Get Route Stop Detail
	 * @apiName getRouteStopDetail
	 * @apiParam {boolean} [includeParcels] Include Parcels
	 * @apiParam {boolean} [includeStatusCodes] Include Status Codes
	 * @apiParam {boolean} [includeSignature] Include Signature
	 * @apiParam {boolean} [includeAttachments] Include Attachments
	 * @apiParam {boolean} [includeParcelAssignmentItems] Include Parcel Assignment Items
	 * @apiGroup RouteStops
	 * @apiVersion 20.0.0
	 */
	getRouteStopDetail(id, includeParcels, includeStatusCodes, includeSignature) {
		let params: HttpParams = new HttpParams();
		params = params.append('includeParcels', includeParcels);
		params = params.append('includeStatusCodes', includeStatusCodes);
		params = params.append('includeSignature', includeSignature);
		params = params.append('includeAttachments', 'true');
		params = params.append('includeParcelAssignmentItems', 'true');
		return this._http.get<any>(`${environment.apiUrl}/v2/RouteTracking/${id}`, {params});
	}

	/**
	 * @api {get} /v2/RouteStops/${id}/${postDate} Get Route Stops
	 * @apiName getRouteStops
	 * @apiGroup RouteStops
	 * @apiVersion 20.0.0
	 */
	getRouteStops(id, postDate) {
		return this._http.get<any>(`${environment.apiUrl}/v2/RouteStops/${id}/${postDate}`);
	}

	/**
	 * @api {post} /v2/RouteStops/Summary/:postDate Get Route Tracking Summary
	 * @apiName getRouteTrackingSummary
	 * @apiParam {number} [pageNum] Page Number
	 * @apiParam {number} [pageSize] Page Size
	 * @apiParam {string} [sortColumn] Sort Column
	 * @apiParam {string} [sortDirection] Sort Direction
	 * @apiGroup RouteStops
	 * @apiVersion 20.0.0
	 */
	getRouteTrackingSummary(postDate, page, size, search, sort, dir) {
		let params: HttpParams = new HttpParams();
		params = params.append('pageNum', page);
		params = params.append('pageSize', size);
		if (sort) {
			params = params.append('sortColumn', sort);
		}
		if (dir) {
			params = params.append('sortDirection', dir);
		}
		return this._http.post<any>(`${environment.apiUrl}/v2/RouteStops/Summary/${postDate}`, JSON.stringify(search), {params});
	}

	/**
	 * @api {put} /v2/RouteStops/:id/StatusEvent Update Route Stop
	 * @apiName getRouteStops
	 * @apiGroup RouteStops
	 * @apiVersion 20.0.0
	 */
	updateRouteStop(id, date, pod, action, rowVersion) {
		const data = {
			occurredAt: date,
			statusEvent: action,
			pod,
			rowVersion
		};
		return this._http.put<any>(`${environment.apiUrl}/v2/RouteStops/${id}/StatusEvent`, data);
	}
   /**
   * @api {get} v2/RouteTracking/{stopId}/Parcels get parcels by route stop id
   * @apiName getParcelsByStopId
   * @apiGroup RouteStops
   * @apiVersion 20.0.0
   */
     getParcelsByStopId(stopId, filter, pageNum, pageSize) {
      return this._http.get<any>(`${environment.apiUrl}/v2/RouteTracking/${stopId}/Parcels?filter=${filter || ''}&pageNum=${pageNum}&pageSize=${pageSize}`);
    }

	/**
     * @api {post} /v2/Routes/{routeId}/postdate/{postDate}/ShippingLabels/Email Email Shipping Labels for Route
     * @apiName emailAllShippingLabels
     * @apiGroup Routes
     * @apiVersion 20.0.0
     */
    emailAllShippingLabels(routeId: string, postDate: string, emailData: any, shippingLabelId?: number) {
      const requestData: any = {
        emailToStops: emailData.emailToStops,
        emailToSpecified: emailData.emailToSpecified,
        subject: emailData.subject,
        body: emailData.body,
        emails: emailData.emails
      };

      let params = new HttpParams();
      if (shippingLabelId != null) {
        params = params.append('shippingLabelId', shippingLabelId.toString());
      }

      return this._http.post(`${environment.apiUrl}/v2/Routes/${routeId}/postdate/${postDate}/ShippingLabels/Email`, requestData, { params });
    }

	/**
     * @api {get} /v2/RouteStops/{routeStopId}/ShippingLabels/PDF Get Shipping Labels PDF for Route Stop
     * @apiName getRouteStopShippingLabelsPDF
     * @apiGroup RouteStops
     * @apiVersion 20.0.0
     */
    getRouteStopShippingLabelsPDF(routeStopId: string, shippingLabelId?: number): Observable<Blob> {
        let params = new HttpParams();
        if (shippingLabelId != null) {
            params = params.append('shippingLabelId', shippingLabelId.toString());
        }
        return this._http.get(`${environment.apiUrl}/v2/RouteStops/${routeStopId}/ShippingLabels/PDF`, {
            params,
            responseType: 'blob'
        });
    }

	/**
     * @api {post} /v2/Routes/{routeId}/{routeStopId}/ShippingLabels/Email Email Shipping Labels for Route Stop
     * @apiName emailRouteStopShippingLabels
     * @apiGroup Routes
     * @apiVersion 20.0.0
     */
    emailRouteStopShippingLabels(routeId: string, routeStopId: string, emailData: { subject: string, body: string, emails: string[] }, shippingLabelId?: number) {
      let params = new HttpParams();
      if (shippingLabelId != null) {
        params = params.append('shippingLabelId', shippingLabelId.toString());
      }
      return this._http.post(`${environment.apiUrl}/v2/Routes/${routeId}/${routeStopId}/ShippingLabels/Email`, emailData, { params });
    }
}
